import React, { useState } from "react";
import { SEARCH } from "../../../core/theme/searchTheme";
import {
  searchNiches, clearNicheMatch, isExactNiche, existingNicheForSuggestion, nichesFittingKeywords, UNSORTED_NICHE_NAME,
} from "../../../core/data/niches";

// Niche, filled in from the keywords. Change opens one type-to-search box
// offering only niches the keywords fit (every niche if none fit);
// the user can also suggest a new niche (the scan runs as Unsorted).
export default function NicheSection({ form, plan, niches, nicheJobs, saveSuggestion }) {
  const [changing, setChanging] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const allowed = nichesFittingKeywords(plan.standardForms, niches);
  const results = changing ? searchNiches(query, allowed) : [];
  // "Suggest a niche" is offered unless the text is exactly an existing niche or synonym.
  const canSuggest = changing && query.trim() !== "" && !isExactNiche(query, niches);
  // Typed an existing niche the keywords don't fit, e.g. "accountants" for plumber keywords.
  const doesntFit = changing && query.trim() !== "" && results.length === 0 && !canSuggest;

  const close = () => { setChanging(false); setQuery(""); setError(""); };

  const choose = (niche) => {
    form.setNicheChoice({ niche });
    close();
  };

  const suggest = async () => {
    const text = query.trim();
    if (!text) return;
    const existing = existingNicheForSuggestion(text, niches);
    if (existing) {
      if (allowed.some(n => n.id === existing.id)) return choose(existing);
      setError(`"${text}" is ${existing.name}, which doesn't fit your keywords.`);
      return;
    }
    setSaving(true);
    const id = await saveSuggestion(text);
    setSaving(false);
    if (!id) {
      setError("Couldn't save that suggestion. Try again.");
      return;
    }
    form.setNicheChoice({ suggestion: { id, text } });
    close();
  };

  const nicheLabel = plan.niche && plan.niche.name !== UNSORTED_NICHE_NAME
    ? `${plan.niche.name}${plan.subGroup ? ` → ${plan.subGroup.name}` : ""}`
    : null;
  const showUseSet = nicheLabel && nicheJobs.count > 0 && nicheJobs.latestKeywords.length > 0;

  return (
    <div>
      <div className={SEARCH.sectionHeader}>
        <span className={SEARCH.sectionTitle}>Niche</span>
        <span className={SEARCH.sectionHint}>Filled in from your keywords</span>
      </div>
      <div className={SEARCH.nicheBox}>
        {!changing ? (
          <>
            <div className="flex items-center justify-between gap-3">
              <p className={nicheLabel || plan.suggestion ? SEARCH.nicheText : SEARCH.nicheMuted}>
                {plan.suggestion ? (
                  <>Suggested <span className={SEARCH.nicheStrong}>"{plan.suggestion.text}"</span> · runs as Unsorted</>
                ) : nicheLabel ? (
                  <>{plan.isAutoNiche ? "Looks like " : ""}<span className={SEARCH.nicheStrong}>{nicheLabel}</span></>
                ) : form.keywords.length > 0 ? "No niche matched · runs as Unsorted" : "Add a keyword to fill this in"}
              </p>
              <div className="flex items-center gap-3 shrink-0">
                {form.nicheChoice && <button className={SEARCH.linkButton} onClick={() => form.setNicheChoice(null)}>Auto</button>}
                <button className={SEARCH.linkButton} onClick={() => setChanging(true)}>Change</button>
              </div>
            </div>
            {showUseSet && (
              <p className="text-xs text-slate-500 mt-1">
                Matches your last {nicheJobs.count} {plan.niche.name} snapshot{nicheJobs.count > 1 ? "s" : ""} ·{" "}
                <button className={SEARCH.linkButton} onClick={() => form.addKeywords(nicheJobs.latestKeywords)}>Use your set</button>
              </p>
            )}
          </>
        ) : (
          <div className="relative">
            <div className="flex items-center gap-2">
              <input
                autoFocus
                className={SEARCH.inputSmall}
                placeholder="Type a niche, e.g. dentist"
                value={query}
                onChange={e => { setQuery(e.target.value); setError(""); }}
                onKeyDown={e => {
                  if (e.key === "Escape") close();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const clear = clearNicheMatch(query, allowed);
                    if (clear) choose(clear);
                    else if (canSuggest) suggest();
                  }
                }}
              />
              <button className={SEARCH.linkButton} onClick={close}>Cancel</button>
            </div>
            <div className={SEARCH.dropdown}>
              {results.map(n => (
                <button key={n.id} className={SEARCH.dropdownItem} onClick={() => choose(n)}>{n.name}</button>
              ))}
              {canSuggest && (
                <div className={SEARCH.suggestRow}>
                  <span className="truncate">Not listed? <span className={SEARCH.nicheStrong}>"{query.trim()}"</span></span>
                  <button className={SEARCH.suggestButton} onClick={suggest} disabled={saving}>
                    {saving ? "Saving..." : "Suggest niche"}
                  </button>
                </div>
              )}
              {doesntFit && !error && <p className={SEARCH.dropdownNote}>That niche doesn't fit your keywords.</p>}
              {error && <p className={SEARCH.dropdownError}>{error}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
